"use client";

import { IoIosArrowForward } from "@react-icons/all-files/io/IoIosArrowForward";
import { IoMdClose } from "@react-icons/all-files/io/IoMdClose";
import Image from "next/image";
import Link from "next/link";
import React, { Suspense, useState } from "react";
import { IBlogPage, INewCategory } from "../../types";
import { NewsLetterForm } from "../newsLetterForm/newsLetterForm";
import { StyledDiv } from "./blogPosts.styles";
import { BlogCardDate } from "./blogCardDate.comp";
import { Button } from "../button/button.comp";
import { Pagination } from "../pagination/pagination";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { CategoryPills } from "../categoryPills/categoryPills.comp";
import { BLOG_PAGE_SIZE } from "../../helpers/blogPageSize";

interface IBlogPostsProps {
  blogPosts: IBlogPage[];
  category?: string;
  categories?: INewCategory[];
  // Set on the prerendered /blog/page/<n> routes, so page n is in the static HTML.
  initialPage?: number;
}

const pageSize = BLOG_PAGE_SIZE;

export const BlogPosts = ({
  blogPosts,
  category,
  categories,
  initialPage,
}: IBlogPostsProps) => {
  return (
    <Suspense>
      <PostsCatalog
        blogPosts={blogPosts}
        category={category}
        categories={categories}
        initialPage={initialPage}
      />
    </Suspense>
  );
};

function PostsCatalog({
  blogPosts,
  category,
  categories,
  initialPage,
}: IBlogPostsProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [filter, setFilter] = useState({
    name: "",
  });

  // A search filters on this page and paginates with ?page=, so it never leaves the current route.
  const searching = !!filter.name;
  const page = searchParams.get("page") || (searching ? null : initialPage);
  const basePath = `/blog/${category ? `category/${category}` : ""}`;

  const filteredPosts = blogPosts
    .filter((post) => {
      return post.name.toLowerCase().includes(filter.name.toLowerCase());
    })
    .filter((p) => {
      if (!category) return true;
      const cats = (p.categories || []).map((c) =>
        c?.name?.toLowerCase?.()?.replaceAll?.(" ", "-")
      );
      return cats.includes(category);
    });

  function onPaginate(page: number) {
    if (page === 1) {
      router.replace(pathname);
      return;
    }
    router.replace(`${pathname}?page=${page}`);
  }

  function pageHref(page: number) {
    if (page === 1) return basePath.replace(/\/$/, "") || "/blog";
    return `${basePath.replace(/\/$/, "")}/page/${page}`;
  }

  const startIndex = (Number(page || 1) - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedPosts = filteredPosts.slice(startIndex, endIndex);
  const totalPages =
    Math.ceil((filteredPosts.length + pageSize - 1) / pageSize) - 1;

  const primaryCategory = (post: IBlogPage) => post.categories?.[0]?.name;

  return (
    <>
      {categories && categories.length > 0 && (
        <CategoryPills categories={categories} activeSlug={category} />
      )}
      <StyledDiv>
        <form
          className="search-components"
          onSubmit={(e) => e.preventDefault()}
        >
          <input
            title="Search Posts"
            placeholder="Search for a blog post..."
            value={filter.name}
            onChange={(e) => {
              setFilter({ ...filter, name: e.target.value });
              router.replace(pathname);
            }}
          />
          {filter.name && (
            <IoMdClose
              onClick={() => {
                setFilter({ ...filter, name: "" });
                router.replace(pathname);
              }}
              className="reset"
              fontSize={20}
            />
          )}
        </form>
        <section className="posts">
          {(paginatedPosts || []).length > 0 && (
            <Link
              title={paginatedPosts[0].name}
              href={`/blog/${paginatedPosts[0].slug}`}
              className="first-post-link"
            >
              <div className={`post first-post`}>
                <div className="image-wrap">
                  <Image
                    src={paginatedPosts[0].coverImage}
                    alt={paginatedPosts[0].name}
                    width={600}
                    height={375}
                  />
                  <span className="featured-badge">Featured</span>
                </div>
                <div className="details">
                  {primaryCategory(paginatedPosts[0]) && (
                    <span className="cat-tag">
                      {primaryCategory(paginatedPosts[0])}
                    </span>
                  )}
                  <h3>{paginatedPosts[0].name}</h3>
                  <p className="author">
                    {paginatedPosts[0].author},
                    <BlogCardDate post={paginatedPosts[0]} />
                  </p>
                  <div
                    className="desc"
                    dangerouslySetInnerHTML={{
                      __html: paginatedPosts[0].excerpt,
                    }}
                  ></div>
                  <button className="read-more">
                    Read More
                    <IoIosArrowForward className="icon" />
                  </button>
                </div>
              </div>
            </Link>
          )}
          <div className="posts-container">
            {(blogPosts || []).length > 0 &&
              (paginatedPosts || []).length > 0 &&
              paginatedPosts.slice(1).map((post, idx) => (
                <Link
                  title={post.name}
                  key={post.name}
                  href={`/blog/${post.slug}`}
                >
                  <div className={`post`}>
                    <div className="image-wrap">
                      <Image
                        src={post.coverImage}
                        alt={post.name}
                        width={700}
                        height={350}
                      />
                      {primaryCategory(post) && (
                        <span className="cat-tag floating">
                          {primaryCategory(post)}
                        </span>
                      )}
                    </div>
                    <div className="details">
                      <h3>{post.name}</h3>
                      <p className="author">
                        {post.author},
                        <BlogCardDate post={post} />
                      </p>
                      <div
                        className="desc"
                        dangerouslySetInnerHTML={{ __html: post.excerpt }}
                      ></div>
                      <button className="read-more">
                        Read More
                        <IoIosArrowForward className="icon" />
                      </button>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
          {!filteredPosts.length && (
            <div className="no-items">
              No posts found matching your search criteria.
              <Button
                onClick={(e) => {
                  setFilter({ ...filter, name: "" });
                  router.replace(pathname);
                }}
                variant="secondary"
              >
                Clear Search
              </Button>
            </div>
          )}
        </section>
      </StyledDiv>
      <Pagination
        currentPage={Number(page) || 1}
        onPageChange={onPaginate}
        totalCount={totalPages}
        pageSize={1}
        getHref={searching ? undefined : pageHref}
      />
      <NewsLetterForm />
    </>
  );
}
